import React from 'react';
import {
    ChevronRight, ChevronDown, Clock, Calendar, Sparkles, HeartHandshake, Camera,
    MessageSquare, MessageCircle, Car, Star, Lock, ListChecks, Music, Plus, Trophy, Users, Upload, Video, Check, ArrowRight, Images, Heart
} from "lucide-react";
import { format, parseISO, isPast } from "date-fns";
import { de } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";
import API, { getAuthHeaders, BACKEND_URL } from "@/lib/api";
import { useRef, useState, useEffect } from "react";

const GuestFeed = ({ onNavigate, guests = [], timeline = [], event, visibleFeatures = [], rsvpStatus, onOpenFeature, isOwner, guestName, summary, onGenerateSummary, isGeneratingSummary, onViewSummary }) => {
    const cameraInputRef = useRef(null);
    const photoInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const musicInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadType, setUploadType] = useState(null);
    const [mediaData, setMediaData] = useState({
        photos: [],
        videos: [],
        music: [],
        guestbook: [],
        rides: [],
        tasks: [],
        wishlist: [],
        leaderboard: [],
        dish_voting: [],
        chat: [],
        selfieWinners: {}
    });

    const [scrollY, setScrollY] = useState(0);
    const [hasVisited, setHasVisited] = useState(false);

    useEffect(() => {
        if (!event?.id) return;
        const key = `visited_${event.id}`;
        if (localStorage.getItem(key)) {
            setHasVisited(true);
        } else {
            // Mark as visited after 10 seconds of interaction
            const timer = setTimeout(() => {
                localStorage.setItem(key, 'true');
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [event?.id]);

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Fetch media and feature data when event changes
    // Fetch media and feature data when event changes
    useEffect(() => {
        if (!event?.id) return;

        const fetchData = async () => {
            try {
                // Performance Optimization: Use single aggregated endpoint
                const shareCodeForApi = event.shareCode || event.share_code;

                if (shareCodeForApi) {
                    // Use new efficient public endpoint
                    const res = await axios.get(`${BACKEND_URL}/api/public/event/${shareCodeForApi}/feed`);
                    setMediaData({
                        photos: Array.isArray(res.data.photos) ? res.data.photos : [],
                        videos: Array.isArray(res.data.videos) ? res.data.videos : [],
                        music: Array.isArray(res.data.music) ? res.data.music : [],
                        guestbook: Array.isArray(res.data.guestbook) ? res.data.guestbook : [],
                        rides: Array.isArray(res.data.rides) ? res.data.rides : [],
                        tasks: Array.isArray(res.data.tasks) ? res.data.tasks : [],
                        wishlist: Array.isArray(res.data.wishlist) ? res.data.wishlist : [],
                        leaderboard: Array.isArray(res.data.leaderboard) ? res.data.leaderboard : [],
                        dish_voting: Array.isArray(res.data.dish_voting) ? res.data.dish_voting : [],
                        chat: Array.isArray(res.data.chat) ? res.data.chat : [],
                        selfieWinners: res.data.selfieWinners || {}
                    });
                } else {
                    // Fallback for non-public view (Creator Dashboard internal view)
                    // We can keep using individual endpoints or build an internal aggregated one.
                    // For now, let's keep the safe fallback or just use the same logic if we have ID.
                    // Actually, let's just use the parallel fetch here as fallback for robustness
                    const [
                        photosRes, videosRes, musicRes, guestbookRes, ridesRes, tasksRes,
                        wishlistRes, leaderboardRes, dishVotingRes, chatRes, selfieWinnersRes
                    ] = await Promise.all([
                        axios.get(`${API}/events/${event.id}/photos`).catch(() => ({ data: [] })),
                        axios.get(`${API}/events/${event.id}/videos`).catch(() => ({ data: [] })),
                        axios.get(`${API}/playlists/events/${event.id}/playlist`).catch(() => ({ data: { songs: [] } })),
                        axios.get(`${API}/events/${event.id}/guestbook`).catch(() => ({ data: [] })),
                        axios.get(`${API}/events/${event.id}/carpool`).catch(() => ({ data: [] })),
                        axios.get(`${API}/events/${event.id}/tasks`).catch(() => ({ data: [] })),
                        axios.get(`${API}/events/${event.id}/wishlist`).catch(() => ({ data: [] })),
                        axios.get(`${API}/events/${event.id}/gamification/leaderboard`).catch(() => ({ data: [] })),
                        axios.get(`${API}/events/${event.id}/dish-voting`).catch(() => ({ data: [] })),
                        axios.get(`${API}/events/${event.id}/chat`).catch(() => ({ data: [] })),
                        axios.get(`${API}/events/${event.id}/selfies/winners`).catch(() => ({ data: {} }))
                    ]);

                    setMediaData({
                        photos: Array.isArray(photosRes.data) ? photosRes.data : [],
                        videos: Array.isArray(videosRes.data) ? videosRes.data : [],
                        music: (Array.isArray(musicRes.data?.songs) ? musicRes.data.songs : []) || [],
                        guestbook: Array.isArray(guestbookRes.data) ? guestbookRes.data : [],
                        rides: Array.isArray(ridesRes.data) ? ridesRes.data : [],
                        tasks: Array.isArray(tasksRes.data) ? tasksRes.data : [],
                        wishlist: Array.isArray(wishlistRes.data) ? wishlistRes.data : [],
                        leaderboard: Array.isArray(leaderboardRes.data) ? leaderboardRes.data : [],
                        dish_voting: Array.isArray(dishVotingRes.data) ? dishVotingRes.data : [],
                        chat: Array.isArray(chatRes.data) ? chatRes.data : [],
                        selfieWinners: selfieWinnersRes.data || {}
                    });
                }
            } catch (error) {
                console.error('Failed to fetch feed data:', error);
            }
        };

        fetchData();
        // Polling every 15 seconds instead of 10 to save further resources
        const interval = setInterval(fetchData, 15000);
        return () => clearInterval(interval);
    }, [event?.id, event?.share_code, event?.shareCode]);


    const handleCameraUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            toast.error("Bild ist zu groß (Max 10MB)");
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('photo', file); // Public endpoint expects 'photo' field

        try {
            const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
            const headers = {
                'Content-Type': 'multipart/form-data'
            };
            if (token) headers.Authorization = `Bearer ${token}`;

            const shareCodeForApi = event.shareCode || event.share_code;
            if (shareCodeForApi) {
                // Use PUBLIC upload endpoint
                await axios.post(`${BACKEND_URL}/api/public/event/${shareCodeForApi}/photos`, formData, {
                    headers,
                    params: { guest_name: guestName || 'Gast' }
                });
            } else {
                // Fallback to PRIVATE media upload if no share code
                formData.delete('photo');
                formData.append('file', file); // media/upload expects 'file'
                await axios.post(`${API}/events/${event.id}/media/upload`, formData, { headers });
            }

            toast.success("Foto hochgeladen!");
        } catch (error) {
            console.error("Upload failed", error);
            toast.error("Upload fehlgeschlagen");
        } finally {
            setIsUploading(false);
            if (cameraInputRef.current) cameraInputRef.current.value = "";
        }
    };
    const handleDishVote = async (dishId) => {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
            const headers = getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            await axios.post(`${API}/events/${event.id}/dish-voting/${dishId}/vote`, {}, { headers });

            toast.success("Abgestimmt! 🍽️");

            // Optimistic update
            const updatedDishes = (mediaData.dish_voting || []).map(d => {
                if (d.id === dishId) return { ...d, votes: (d.votes || 0) + 1 };
                return d;
            });
            setMediaData(prev => ({ ...prev, dish_voting: updatedDishes }));

        } catch (error) {
            console.error("Voting failed", error);
            if (error.response?.status === 400) {
                toast.error("Du hast bereits abgestimmt!");
            } else {
                toast.error("Fehler beim Abstimmen");
            }
        }
    };

    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setIsUploading(true);
        setUploadType('photo');

        try {
            for (const file of files) {
                if (file.size > 20 * 1024 * 1024) {
                    toast.error(`${file.name} ist zu groß (max. 20MB)`);
                    continue;
                }
                const formData = new FormData();
                formData.append('photo', file);
                formData.append('guest_name', 'Event Owner');

                const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
                const headers = { 'Content-Type': 'multipart/form-data' };
                if (token) headers.Authorization = `Bearer ${token}`;

                await axios.post(`${API}/events/${event.id}/photos`, formData, { headers });
            }
            toast.success(`${files.length} Foto(s) hochgeladen! 📸`);
        } catch (error) {
            console.error('Photo upload failed', error);
            toast.error('Upload fehlgeschlagen');
        } finally {
            setIsUploading(false);
            setUploadType(null);
            if (photoInputRef.current) photoInputRef.current.value = "";
        }
    };

    const handleVideoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 100 * 1024 * 1024) {
            toast.error('Video ist zu groß (max. 100MB)');
            return;
        }

        setIsUploading(true);
        setUploadType('video');

        try {
            const formData = new FormData();
            formData.append('video', file);

            const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
            const headers = { 'Content-Type': 'multipart/form-data' };
            if (token) headers.Authorization = `Bearer ${token}`;

            await axios.post(`${API}/events/${event.id}/videos`, formData, { headers });
            toast.success('Video hochgeladen! 🎬');
        } catch (error) {
            console.error('Video upload failed', error);
            toast.error('Upload fehlgeschlagen');
        } finally {
            setIsUploading(false);
            setUploadType(null);
            if (videoInputRef.current) videoInputRef.current.value = "";
        }
    };

    const handleMusicUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 50 * 1024 * 1024) {
            toast.error('Musikdatei ist zu groß (max. 50MB)');
            return;
        }

        if (!file.type.startsWith('audio/')) {
            toast.error('Bitte nur Audiodateien hochladen');
            return;
        }

        setIsUploading(true);
        setUploadType('music');

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('added_by_name', 'Event Owner');

            const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
            const headers = { 'Content-Type': 'multipart/form-data' };
            if (token) headers.Authorization = `Bearer ${token}`;

            await axios.post(`${API}/playlists/events/${event.id}/playlist/upload`, formData, { headers });
            toast.success('Song hochgeladen! 🎵');
        } catch (error) {
            console.error('Music upload failed', error);
            toast.error('Upload fehlgeschlagen');
        } finally {
            setIsUploading(false);
            setUploadType(null);
            if (musicInputRef.current) musicInputRef.current.value = "";
        }
    };

    // Logic to find next program point
    const getNextProgramPoint = () => {
        if (!timeline || timeline.length === 0) return null;
        const now = new Date();
        // Filter for future events or events happening within last hour (active)
        const upcoming = timeline.filter(t => {
            const tDate = parseISO(t.datetime);
            return tDate > now || (now - tDate < 60 * 60 * 1000);
        });
        return upcoming.length > 0 ? upcoming[0] : null;
    };

    const nextPoint = getNextProgramPoint();
    const isUnlocked = rsvpStatus === 'accepted' || isOwner;

    // Helper to render specific widget styles
    const renderFeatureWidget = (feature, index) => {
        const isLocked = !isUnlocked;

        // Determine span layout based on feature type
        // REMOVED 'index % 5 === 0' to ensure uniform grid as requested ("alle Kacheln mit gleichem Abstand")
        // Only specific large content types get full width.
        const isFullWidth = ['location', 'video_upload', 'timeline'].includes(feature.key);

        // Widget Content Switcher
        const renderWidgetContent = () => {
            switch (feature.key) {
                case 'chat':
                    const latestMessage = mediaData.chat?.[mediaData.chat.length - 1];
                    return (
                        <div className="flex flex-col justify-between h-full bg-white p-4 md:p-6 relative overflow-hidden group">
                            <div className="absolute inset-0 bg-blue-50/50" />
                            <div className="relative z-10 flex items-center justify-between mb-4">
                                <span className="text-[10px] font-black uppercase tracking-widest text-blue-500">Chat</span>
                                <MessageCircle className="w-4 h-4 text-blue-400" />
                            </div>
                            <p className="relative z-10 font-bold text-lg text-gray-800 leading-tight mb-4 line-clamp-2">
                                {latestMessage ? (latestMessage.content?.includes('[Bild:') ? "📷 Bild gesendet" : latestMessage.content) : "Noch keine Nachrichten..."}
                            </p>
                            <div className="relative z-10 flex items-center gap-2 mt-auto">
                                <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center overflow-hidden">
                                    {latestMessage?.sender_picture ? (
                                        <img src={latestMessage.sender_picture} className="w-full h-full object-cover" alt="" />
                                    ) : (
                                        <div className="w-full h-full bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-500 uppercase">
                                            {latestMessage?.sender_name?.charAt(0) || '?'}
                                        </div>
                                    )}
                                </div>
                                <span className="text-xs font-bold text-gray-400 group-hover:text-blue-500 transition-colors">
                                    {latestMessage ? latestMessage.sender_name : 'Schreib etwas...'}
                                </span>
                            </div>
                        </div>
                    );
                case 'location':
                    return (
                        <div className="h-full flex flex-col justify-end text-white relative z-10 p-4 md:p-6">
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                            <div className="relative z-10">
                                <span className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-1 block">Standort</span>
                                <h4 className="font-black text-2xl uppercase leading-none drop-shadow-md truncate">
                                    {event?.location?.split(',')[0] || "Event Location"}
                                </h4>
                                <div className="mt-4">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(`https://maps.google.com/?q=${encodeURIComponent(event.location)}`, '_blank');
                                        }}
                                        className="bg-white/20 backdrop-blur-md hover:bg-white/30 border border-white/30 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                                    >
                                        Google Maps öffnen
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                case 'guestbook':
                    const latestEntry = mediaData.guestbook?.[0];
                    return (
                        <div className="flex flex-col justify-between h-full bg-white p-6 relative overflow-hidden group">
                            <div className="absolute inset-0 bg-orange-50/50" />
                            <div className="relative z-10 flex items-center justify-between mb-4">
                                <span className="text-[10px] font-black uppercase tracking-widest text-orange-500">Gästebuch</span>
                                <MessageSquare className="w-4 h-4 text-orange-400" />
                            </div>
                            <p className="relative z-10 font-serif italic text-lg text-gray-700 leading-relaxed mb-4 line-clamp-3">
                                {latestEntry ? `"${latestEntry.message}"` : '"Wir freuen uns so sehr auf diesen Tag!"'}
                            </p>
                            <div className="relative z-10 flex items-center gap-2 mt-auto">
                                <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-[10px] font-bold text-orange-400 border border-orange-100 uppercase">
                                    {latestEntry?.guest_name?.charAt(0) || '?'}
                                </div>
                                <span className="text-xs font-bold text-gray-400 group-hover:text-orange-500 transition-colors">
                                    {latestEntry ? latestEntry.guest_name : 'Nachricht schreiben...'}
                                </span>
                            </div>
                        </div>
                    );
                case 'carpool':
                    const ridesCount = mediaData.rides?.length || 0;
                    const latestRide = mediaData.rides?.[mediaData.rides.length - 1];
                    return (
                        <div className="flex flex-col justify-between h-full bg-emerald-50 p-4 md:p-6 relative overflow-hidden group">
                            <Car className="absolute -bottom-4 -right-4 w-24 h-24 text-emerald-100 -rotate-12 group-hover:-rotate-6 transition-transform" />
                            <div className="relative z-10">
                                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Mobilität</span>
                                <h4 className="font-black text-gray-900 text-xl md:text-2xl mt-1">Fahrgemeinschaft</h4>
                                <p className="text-xs md:text-sm text-emerald-700/70 mt-1 font-medium leading-relaxed">
                                    {ridesCount > 0 ? `${ridesCount} Angebot${ridesCount === 1 ? '' : 'e'} verfügbar.` : 'Finde oder biete eine freie Fahrt.'}
                                </p>
                            </div>
                            {ridesCount > 0 && latestRide ? (
                                <div className="flex items-center gap-4 mt-4 bg-white/70 backdrop-blur-md p-3 rounded-2xl text-xs font-bold text-emerald-600 transition-all group-hover:bg-white shadow-sm border border-white/50">
                                    <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm flex-shrink-0 shadow-lg border-2 border-white">
                                        {latestRide.driver_name?.charAt(0) || "F"}
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-gray-900 truncate text-sm md:text-base font-black">Von: {latestRide.departure_location || "Unbekannt"}</span>
                                        <span className="text-[10px] md:text-xs opacity-70 truncate font-bold text-emerald-800">By {latestRide.driver_name?.split(' ')[0] || "Gast"} • {latestRide.available_seats} freie Plätze</span>
                                    </div>
                                </div>
                            ) : (
                                <span className="inline-block py-2 px-5 bg-white text-emerald-700 text-[10px] font-black uppercase tracking-[0.1em] rounded-xl shadow-sm group-hover:shadow-md transition-all">
                                    Fahrt anbieten
                                </span>
                            )}
                        </div>
                    );
                case 'tasks':
                    const allOpenTasks = mediaData.tasks?.filter(t => !t.assigned_to && !t.completed) || [];
                    const displayTasks = allOpenTasks.slice(0, 3);

                    return (
                        <div className="flex flex-col justify-between h-full bg-white p-3 md:p-5 relative overflow-hidden group">
                            <div className="absolute -right-4 -top-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <ListChecks className="w-32 h-32 rotate-12 text-indigo-600" />
                            </div>
                            <div className="relative z-10 mb-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-500">Aufgaben</span>
                                <h4 className="font-bold text-gray-900 leading-tight mt-1 text-lg">Helfende Hände</h4>
                            </div>

                            <div className="relative z-10 space-y-2 flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
                                {displayTasks.length > 0 ? (
                                    displayTasks.map((task, i) => (
                                        <div key={i} className="bg-indigo-50/50 p-2 rounded-lg border border-indigo-100/50 flex items-center gap-2 group/task hover:bg-indigo-50 transition-colors cursor-pointer" onClick={() => onOpenFeature('tasks')}>
                                            <div className="w-3 h-3 rounded-sm border-2 border-indigo-300 bg-white flex-shrink-0 group-hover/task:border-indigo-400" />
                                            <span className="text-xs font-medium text-gray-700 truncate">{task.title}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center text-center opacity-60">
                                        <Check className="w-8 h-8 text-indigo-300 mb-1" />
                                        <span className="text-xs font-medium text-gray-500">Alles erledigt!</span>
                                    </div>
                                )}

                                {allOpenTasks.length > 3 && (
                                    <div className="text-[10px] text-indigo-400 font-bold text-center pt-1">
                                        +{allOpenTasks.length - 3} weitere
                                    </div>
                                )}
                            </div>

                            <div className="relative z-10 mt-3 pt-2 border-t border-gray-100">
                                <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider flex items-center gap-1 group-hover:text-indigo-600 transition-colors">
                                    Pack mit an! <ArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        </div>
                    );
                case 'dresscode':
                    const hasAIImages = event?.dresscode_images && event.dresscode_images.length > 0;
                    const selectedInspiration = event?.dresscode_image;
                    const displayImages = event?.dresscode_images || [];

                    return (
                        <div className="h-full flex flex-col justify-end text-white relative z-10 p-6 overflow-hidden">
                            {selectedInspiration ? (
                                <>
                                    <img src={selectedInspiration} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
                                </>
                            ) : (
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                            )}

                            <div className="relative z-10">
                                <span className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-1 block">Style Guide</span>
                                <h4 className="font-black text-2xl uppercase leading-none drop-shadow-md">
                                    {event?.dresscode_style || "Dresscode"}
                                </h4>
                                <div className="flex items-center gap-2 mt-2">
                                    {displayImages.length > 0 && (
                                        <div className="flex -space-x-2">
                                            {displayImages.slice(0, 3).map((img, i) => (
                                                <div key={i} className={`w-5 h-5 rounded-full border border-white/50 overflow-hidden bg-gray-800 ${img === selectedInspiration ? 'ring-2 ring-brand-accent' : ''}`}>
                                                    <img src={img} className="w-full h-full object-cover" alt="" />
                                                </div>
                                            ))}
                                            {displayImages.length > 3 && (
                                                <div className="w-5 h-5 rounded-full border border-white/50 bg-white/20 backdrop-blur flex items-center justify-center text-[7px] font-bold">
                                                    +{displayImages.length - 3}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <p className="text-xs font-bold text-white/70 uppercase tracking-widest">
                                        {selectedInspiration ? 'Inspiration ansehen' : (hasAIImages ? 'Inspiration wählen' : 'Details ansehen')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    );
                case 'dish_voting':
                case 'face_voting':
                case 'poll':
                    const dishes = mediaData.dish_voting || [];
                    const totalVotes = dishes.reduce((sum, d) => sum + (d.votes || 0), 0);
                    // Show top 2 dishes or placeholders
                    // Show all dishes, scrollable if many
                    const displayDishes = dishes.length > 0 ? dishes : [
                        { dish_name: "Gericht 1", votes: 0 },
                        { dish_name: "Gericht 2", votes: 0 }
                    ];

                    return (
                        <div className="flex flex-col justify-between h-full bg-white p-3 md:p-6 relative overflow-hidden group">
                            {/* Background Images Mosaic */}
                            <div className="absolute inset-0 flex opacity-20 group-hover:opacity-30 transition-opacity">
                                {displayDishes[0]?.image_url && <img src={displayDishes[0].image_url?.startsWith('http') ? displayDishes[0].image_url : (displayDishes[0].image_url?.startsWith('/api') ? `${BACKEND_URL}${displayDishes[0].image_url}` : `${API}${displayDishes[0].image_url}`)} className={`h-full object-cover ${displayDishes.length > 1 ? 'w-1/2' : 'w-full'}`} alt="" />}
                                {displayDishes[1]?.image_url && <img src={displayDishes[1].image_url?.startsWith('http') ? displayDishes[1].image_url : (displayDishes[1].image_url?.startsWith('/api') ? `${BACKEND_URL}${displayDishes[1].image_url}` : `${API}${displayDishes[1].image_url}`)} className="w-1/2 h-full object-cover" alt="" />}
                            </div>

                            <div className="relative z-10 pb-2 bg-white/50 backdrop-blur-sm -mx-6 px-6 pt-2 mb-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-brand-accent mb-1 block">Menu-Voting</span>
                                <h4 className="font-bold text-gray-900 leading-tight text-base">Was sollen wir essen?</h4>
                            </div>
                            <div className="relative z-10 space-y-3 overflow-y-auto max-h-[220px] pb-8 -mr-2 pr-2 custom-scrollbar">
                                {displayDishes.map((dish, i) => {
                                    const percentage = totalVotes > 0 ? Math.round((dish.votes / totalVotes) * 100) : 0;
                                    const isVoted = false; // Could track this if API returns it

                                    return (
                                        <div key={i} className="w-full flex items-center gap-2 group/item">
                                            <div className="flex-1 h-14 bg-gray-50/90 backdrop-blur-sm rounded-xl relative overflow-hidden flex items-center px-3 border border-gray-100 shadow-sm transition-all hover:bg-white hover:shadow-md cursor-pointer"
                                                onClick={(e) => {
                                                    // Only navigate on click if not clicking a button
                                                    onOpenFeature('dish_voting');
                                                }}
                                            >
                                                <div
                                                    className={`absolute left-0 top-0 h-full ${i === 0 ? 'bg-brand-accent/10' : 'bg-orange-500/5'} transition-all duration-1000`}
                                                    style={{ width: `${percentage}%` }}
                                                />
                                                {dish.image_url && (
                                                    <div className="relative z-10 w-10 h-10 rounded-lg overflow-hidden mr-3 border border-white/50 shadow-sm flex-shrink-0">
                                                        <img src={dish.image_url?.startsWith('http') ? dish.image_url : (dish.image_url?.startsWith('/api') ? `${BACKEND_URL}${dish.image_url}` : `${API}${dish.image_url}`)} className="w-full h-full object-cover" alt="" />
                                                    </div>
                                                )}
                                                <div className="relative z-10 flex-1 min-w-0">
                                                    <div className="flex justify-between items-baseline mb-0.5">
                                                        <span className="text-[12px] font-bold text-gray-900 truncate pr-2">{dish.dish_name}</span>
                                                        <span className="text-[10px] font-black text-brand-accent">{percentage}%</span>
                                                    </div>
                                                    <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                                                        <div className={`h-full ${i === 0 ? 'bg-brand-accent' : 'bg-orange-400'}`} style={{ width: `${percentage}%` }} />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Direct Vote Button - Only show if not owner and unlocked */}
                                            {!isOwner && !isLocked && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDishVote(dish.id);
                                                    }}
                                                    className="w-14 h-14 bg-white border-2 border-brand-accent/10 hover:border-brand-accent hover:bg-brand-accent hover:text-white rounded-xl flex items-center justify-center transition-all active:scale-95 shadow-sm text-brand-accent"
                                                    title="Abstimmen"
                                                >
                                                    <Star className="w-6 h-6" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Fade Overlay for scrolling hint */}
                            {dishes.length > 4 && (
                                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent z-20 pointer-events-none flex items-end justify-center pb-2">
                                    <ChevronDown className="w-4 h-4 text-gray-400 animate-bounce" />
                                </div>
                            )}
                        </div>
                    );
                case 'photo_upload':
                    const photoCount = mediaData.photos?.length || 0;
                    const displayPhotos = (mediaData.photos || []).slice(-4).reverse();
                    return (
                        <div className="h-full flex flex-col items-center justify-center relative z-10 p-3 md:p-6 bg-gray-900 text-white text-center group overflow-hidden">
                            {photoCount > 0 ? (
                                <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 opacity-50 group-hover:opacity-70 transition-opacity duration-700">
                                    {displayPhotos.map((p, i) => (
                                        <img
                                            key={i}
                                            src={p.url?.startsWith('http') ? p.url : (p.url?.startsWith('/') ? `${BACKEND_URL}${p.url}` : `${BACKEND_URL}/api/media/${p.url}`)}
                                            className="w-full h-full object-cover border-[0.5px] border-black/20"
                                            alt=""
                                        />
                                    ))}
                                    {/* Fill empty slots if less than 4 photos */}
                                    {Array.from({ length: Math.max(0, 4 - displayPhotos.length) }).map((_, i) => (
                                        <div key={`empty-${i}`} className="bg-gray-800" />
                                    ))}
                                </div>
                            ) : (
                                <div className="absolute inset-0 bg-gray-900" />
                            )}
                            <div className="relative z-10 flex flex-col items-center">
                                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mb-3 text-white border border-white/20 group-hover:scale-110 group-hover:bg-brand-accent group-hover:border-brand-accent transition-all duration-300 shadow-xl">
                                    <Camera className="w-6 h-6" />
                                </div>
                                <h4 className="font-black text-xl md:text-2xl drop-shadow-md">Fotos teilen</h4>
                                {photoCount > 0 ? (
                                    <p className="text-xs md:text-sm font-black uppercase tracking-widest opacity-90 mt-2 bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm">{photoCount} {photoCount === 1 ? 'Foto' : 'Fotos'}</p>
                                ) : (
                                    <p className="text-xs opacity-80 mt-1 max-w-[15ch] font-bold">Werde der Erste!</p>
                                )}
                            </div>
                        </div>
                    );
                case 'music_upload':
                    const latestSong = mediaData.music?.[mediaData.music.length - 1] || null;
                    return (
                        <div className="flex flex-col justify-between h-full bg-gradient-to-br from-violet-600 to-fuchsia-600 p-4 md:p-6 text-white relative overflow-hidden group">
                            <Music className="absolute -bottom-6 -right-6 w-32 h-32 text-white/10 rotate-12 group-hover:rotate-6 transition-transform duration-500" />
                            <div className="relative z-10">
                                <span className="text-[10px] font-black uppercase tracking-widest opacity-80">DJ Pult</span>
                                <h4 className="font-bold text-xl mt-1 leading-tight">Song<br />Wünsche</h4>
                            </div>

                            {latestSong ? (
                                <div className="relative z-10 mt-auto bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/10 flex items-center gap-3 animate-in slide-in-from-bottom-2">
                                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-[10px] flex-shrink-0">🎵</div>
                                    <div className="overflow-hidden min-w-0 text-left">
                                        <p className="text-xs font-bold truncate">{latestSong.title}</p>
                                        <p className="text-[10px] opacity-70 truncate">{latestSong.artist}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="relative z-10 mt-auto">
                                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center group-hover:bg-white group-hover:text-purple-600 transition-all border border-white/20">
                                        <Plus className="w-5 h-5" />
                                    </div>
                                    <p className="text-xs opacity-70 mt-2">Wünsch dir Musik</p>
                                </div>
                            )}
                        </div>
                    );
                case 'video_upload':
                    const videoCount = mediaData.videos?.length || 0;
                    const latestVideo = mediaData.videos?.[mediaData.videos.length - 1];
                    return (
                        <div className="h-full flex flex-col items-center justify-center relative z-10 p-4 md:p-6 bg-gray-900 text-white text-center group overflow-hidden">
                            {latestVideo ? (
                                <div className="absolute inset-0 opacity-40 group-hover:opacity-60 transition-opacity">
                                    <video
                                        src={latestVideo.url?.startsWith('http') ? latestVideo.url : (latestVideo.url?.startsWith('/') ? `${BACKEND_URL}${latestVideo.url}` : `${BACKEND_URL}/api/media/${latestVideo.url}`)}
                                        className="w-full h-full object-cover"
                                        muted
                                        loop
                                        autoPlay
                                        playsInline
                                    />
                                </div>
                            ) : (
                                <div className="absolute inset-0 bg-gray-900" />
                            )}
                            <div className="relative z-10 flex flex-col items-center">
                                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mb-3 text-white border border-white/20 group-hover:scale-110 group-hover:bg-brand-accent group-hover:border-brand-accent transition-all duration-300 shadow-xl">
                                    <Video className="w-6 h-6" />
                                </div>
                                <h4 className="font-black text-xl md:text-2xl drop-shadow-md">Video-Upload</h4>
                                {videoCount > 0 ? (
                                    <p className="text-xs md:text-sm font-black uppercase tracking-widest opacity-90 mt-2 bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm">{videoCount} {videoCount === 1 ? 'Video' : 'Videos'}</p>
                                ) : (
                                    <p className="text-xs opacity-80 mt-1 max-w-[15ch] font-bold">Teile Clips vom Event</p>
                                )}
                            </div>
                        </div>
                    );
                case 'selfie_game':
                    const leadSelfie = mediaData.selfieWinners?.overall;
                    return (
                        <div className="flex flex-col justify-between h-full bg-indigo-50 p-4 md:p-6 relative overflow-hidden group">
                            {leadSelfie ? (
                                <>
                                    <img
                                        src={`${BACKEND_URL}${leadSelfie.image_url}`}
                                        className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-700"
                                        alt="Leader"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/80 via-transparent to-transparent" />
                                    <div className="relative z-10">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-white/80">Selfie-Spiel</span>
                                        <h4 className="font-black text-white text-xl mt-1">Führender: {leadSelfie.author_name}</h4>
                                    </div>
                                    <div className="relative z-10 mt-auto flex items-center gap-2">
                                        <div className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full border border-white/20 text-white text-[10px] font-black flex items-center gap-1">
                                            <Heart className="w-3 h-3 fill-white" />
                                            {leadSelfie.likes} Likes
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Camera className="absolute -bottom-4 -right-4 w-24 h-24 text-indigo-100 -rotate-12 group-hover:-rotate-6 transition-transform" />
                                    <div className="relative z-10">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Event-Spiel</span>
                                        <h4 className="font-black text-gray-900 text-xl md:text-2xl mt-1">Selfie-Spiel</h4>
                                        <p className="text-xs md:text-sm text-indigo-700/70 mt-1 font-medium leading-relaxed max-w-[15ch]">
                                            Verrückte Foto-Challenges
                                        </p>
                                    </div>
                                    <div className="relative z-10 mt-auto">
                                        <span className="inline-block py-2 px-5 bg-white text-indigo-600 text-[10px] font-black uppercase tracking-[0.1em] rounded-xl shadow-sm group-hover:shadow-md transition-all">
                                            Mitmachen
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                case 'guest_who':
                    const confirmedGuests = Array.isArray(guests) ? guests.filter(g => g?.status === 'confirmed') : [];

                    // Simple hook-less sliding logic within the component render using a global timer if needed, 
                    // but better to use a small local state if possible. 
                    // Since this is inside a function component, we should ideally have a parent state or a CSS animation.
                    // Let's use a CSS animation for a "reel" effect.

                    return (
                        <div className="flex flex-col justify-between h-full bg-brand-background p-4 md:p-6 text-white relative overflow-hidden group">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-brand-background opacity-90" />

                            {/* Scrolling Background of Avatars */}
                            <div className="absolute top-0 right-0 h-full w-24 flex flex-col gap-2 py-4 animate-reel opacity-30">
                                {[...confirmedGuests, ...confirmedGuests].slice(0, 10).map((g, i) => (
                                    <div key={i} className="w-12 h-12 rounded-2xl bg-white/20 border border-white/10 flex-shrink-0 overflow-hidden mx-auto">
                                        {g.profile_picture ? (
                                            <img src={g.profile_picture} className="w-full h-full object-cover" alt="" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center font-black text-sm uppercase">{g.name?.charAt(0) || '?'}</div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="relative z-10">
                                <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Gäste</span>
                                <h4 className="font-extrabold text-2xl mt-1 leading-tight tracking-tight">Wer ist<br />dabei?</h4>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60 mt-1">{confirmedGuests.length} Zusagen</p>
                            </div>

                            <div className="relative z-10 mt-auto">
                                <div className="flex -space-x-3">
                                    {confirmedGuests.slice(0, 4).map((g, i) => (
                                        <div key={i} className="w-10 h-10 rounded-2xl border-2 border-brand-background bg-white shadow-xl flex items-center justify-center overflow-hidden transform hover:-translate-y-1 transition-transform">
                                            {g.profile_picture ? (
                                                <img src={g.profile_picture} className="w-full h-full object-cover" alt="" />
                                            ) : (
                                                <span className="text-xs font-black text-brand-accent uppercase">{g.name?.charAt(0) || '?'}</span>
                                            )}
                                        </div>
                                    ))}
                                    {confirmedGuests.length > 4 && (
                                        <div className="w-10 h-10 rounded-2xl border-2 border-brand-background bg-white/20 backdrop-blur-md flex items-center justify-center text-[10px] font-black shadow-xl">
                                            +{confirmedGuests.length - 4}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                default:
                    // Generic Card Enhanced
                    return (
                        <div className="flex flex-col justify-between h-full bg-white p-4 md:p-6 relative overflow-hidden group hover:bg-gray-50 transition-colors">
                            <div className={`absolute top-0 right-0 w-24 h-24 rounded-bl-[4rem] opacity-10 transition-all group-hover:scale-110 group-hover:opacity-20`} style={{ backgroundColor: feature.color || '#000' }} />

                            <div className="relative z-10 w-10 h-10 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center text-gray-900 group-hover:scale-110 transition-transform">
                                {feature.icon || <Star className="w-5 h-5" />}
                            </div>

                            <div className="relative z-10 mt-4">
                                <h4 className="font-bold text-gray-900 text-lg leading-tight group-hover:text-brand-accent transition-colors">{feature.label}</h4>
                                {feature.description && <p className="text-[10px] text-gray-400 font-medium mt-1 leading-relaxed line-clamp-2">{feature.description}</p>}
                            </div>
                        </div>
                    );
            }
        }


        const isFeatureEmpty = (featureKey) => {
            switch (featureKey) {
                case 'guestbook': return !mediaData?.guestbook?.length;
                case 'chat': return !mediaData?.chat?.length;
                case 'carpool':
                case 'rides': return !mediaData?.rides?.length;
                case 'photo_upload':
                case 'gallery': return !mediaData?.photos?.length;
                case 'video_upload': return !mediaData?.videos?.length;
                case 'music_upload':
                case 'audio_upload': return !mediaData?.music?.length;
                case 'wishlist': return !mediaData?.wishlist?.length;
                case 'tasks': return !mediaData?.tasks?.length;
                case 'dish_voting':
                case 'face_voting': return !mediaData?.dish_voting?.length;
                case 'gamification':
                case 'leaderboard': return !mediaData?.leaderboard?.length;
                case 'selfie_game': return !mediaData?.selfieWinners?.overall;
                case 'guest_who': {
                    const validGuests = Array.isArray(guests) ? guests : [];
                    return validGuests.filter(g => g?.status === 'confirmed').length === 0;
                }
                case 'poll':
                case 'date_poll':
                case 'name_advisor':
                case 'gender_guess':
                case 'secret_santa':
                case 'matching_tool':
                case 'team_missions':
                case 'workshop_docs':
                case 'media_download':
                    return true;
                default:
                    return false;
            }
        };

        const isEmpty = isFeatureEmpty(feature.key);
        const hasImageBg = ['dresscode', 'location', 'photo_upload', 'video_upload'].includes(feature.key) && !isEmpty;

        const borderRadius = isEmpty ? 'rounded-[1.2rem] md:rounded-[1.5rem]' : 'rounded-[1.5rem] md:rounded-[2rem]';

        let sizeClasses = '';
        if (isFullWidth) {
            sizeClasses = `col-span-2 md:col-span-3 aspect-[16/9] md:aspect-[3/1] ${borderRadius}`;
        } else {
            // Keep widgets consistently sized as compact squares to prevent grid breakage
            sizeClasses = `col-span-1 md:col-span-1 ${borderRadius} aspect-square`;
        }

        return (
            <div
                key={index}
                onClick={() => isLocked ? toast.error("Bitte sage erst zu!") : onOpenFeature(feature.key)}
                className={`
                relative overflow-hidden transition-all duration-500 transform-gpu
                ${sizeClasses}
                ${hasImageBg ? 'bg-gray-900' : 'bg-white shadow-lg border border-gray-100'}
                ${!isLocked && 'hover:scale-[1.02] hover:shadow-2xl cursor-pointer group'}
            `}
                style={{ position: 'relative', WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
            >
                {/* Background Images */}
                {feature.key === 'dresscode' && (
                    <img
                        src={(event?.dresscode_image ? (event.dresscode_image?.startsWith('http') ? event.dresscode_image : `${BACKEND_URL || API}${event.dresscode_image}`) : event?.dresscode_images?.[0]) || "https://images.unsplash.com/photo-1520342868574-5fa3804e551c?auto=format&fit=crop&q=80"}
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-700"
                        alt=""
                    />
                )}
                {feature.key === 'photo_upload' && (
                    <img
                        src={mediaData.photos?.[mediaData.photos.length - 1] ? `${BACKEND_URL || API}${mediaData.photos[mediaData.photos.length - 1].url}` : "https://images.unsplash.com/photo-1533174072545-e8d4aa97edf9?auto=format&fit=crop&q=80"}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        alt=""
                    />
                )}
                {feature.key === 'location' && (
                    <img src="https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&q=80" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-700" alt="" />
                )}

                {/* Content Wrapper */}
                <div className={`h-full w-full ${hasImageBg ? '' : ''}`}>
                    {renderWidgetContent()}
                </div>

                {/* Lock Overlay */}
                {isLocked && (
                    <div className="absolute inset-0 z-30 bg-white/20 backdrop-blur-md flex flex-col items-center justify-center text-center p-4 transition-all duration-300">
                        <div className="w-12 h-12 rounded-full bg-white shadow-xl flex items-center justify-center mb-2">
                            <Lock className="w-5 h-5 text-gray-900" />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Filter features based on content
    const filteredFeatures = visibleFeatures.filter(feature => {
        // 1. Visitor Fillable / Participatory -> Always Show
        // (These depend on user interaction, not initial creator content)
        // REMOVED 'tasks', 'dish_voting', 'gamification' from here to check content
        const VISITOR_FILLABLE = [
            'guestbook', 'chat', 'carpool', 'rides', 'guest_who',
            'poll',
            'selfie_game',
            'photo_upload', 'gallery', 'video_upload', 'music_upload', 'audio_upload',
            'wishlist', 'secret_santa', 'gender_guess', 'team_missions', 'matching_tool',
            'media_download', 'name_advisor', 'workshop_docs', 'date_poll'
        ];

        // NEW: Hide photo_upload from the standard grid if photos exist, since they are now shown in the top Highlights slider!
        if (['photo_upload', 'gallery'].includes(feature.key) && mediaData.photos && mediaData.photos.length > 0) {
            return false;
        }

        if (VISITOR_FILLABLE.includes(feature.key)) return true;

        // 2. Content Checks for Creator-Filled features OR Data-Dependent features
        switch (feature.key) {
            case 'location':
                return !!event?.location && event.location !== '';
            case 'dresscode':
                return !!(event?.dresscode_style || event?.dresscode_description || (event?.dresscode_images && event?.dresscode_images.length > 0));
            case 'schedule':
            case 'timeline':
                return timeline && timeline.length > 0;
            case 'tips':
                return !!event?.tips;
            case 'budget':
                return !!event?.budget_limit || !!event?.budget_total;
            case 'seating_plan':
                return true;

            // NEW: Data dependent checks
            case 'tasks':
                // Only show if tasks exist
                return mediaData.tasks && mediaData.tasks.length > 0;
            case 'dish_voting':
            case 'face_voting':
                // Only show if dishes exist
                return mediaData.dish_voting && mediaData.dish_voting.length > 0;
            case 'gamification':
            case 'leaderboard':
                // Only show if leaderboard has entries (points exist)
                return mediaData.leaderboard && mediaData.leaderboard.length > 0;

            default:
                // STRICT MODE: If we don't know it, and it's not visitor fillable, HIDE IT.
                return false;
        }
    });

    return (
        <div className="pb-32 space-y-4 animate-in fade-in duration-500">
            <style>{`
                @keyframes reel {
                    0% { transform: translateY(0); }
                    100% { transform: translateY(-50%); }
                }
                .animate-reel {
                    animation: reel 15s linear infinite;
                }
            `}</style>

            {/* 1. Live Updates (Real Guests) */}
            {guests.length > 0 && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center px-2">
                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Live Updates</h3>
                        <button onClick={() => onNavigate('users')} className="text-[10px] font-bold text-brand-accent hover:underline flex items-center gap-1">
                            Alle <ChevronRight className="w-3 h-3" />
                        </button>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-4 px-2 no-scrollbar">
                        {guests.slice(0, 10).map((guest, i) => {
                            const initials = guest.name ? guest.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : "?";
                            return (
                                <div key={i} className="flex flex-col items-center gap-2 min-w-[70px] cursor-pointer hover:scale-105 transition-transform">
                                    <div className={`w-16 h-16 rounded-full p-0.5 ${guest.status === 'confirmed' ? 'bg-gradient-to-tr from-brand-accent to-yellow-200' : 'bg-gray-100'}`} >
                                        {guest.profile_picture ? (
                                            <img src={guest.profile_picture} className="w-full h-full rounded-full object-cover border-2 border-white" alt="" />
                                        ) : (
                                            <div className="w-full h-full rounded-full bg-brand-accent/10 border-2 border-white flex items-center justify-center">
                                                <span className="text-sm font-black text-brand-accent">{initials}</span>
                                            </div>
                                        )}
                                    </div>
                                    <span className="text-[10px] font-bold text-gray-600 truncate w-full text-center">{guest.name}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 2. Next Program Point (Real Data) */}
            {nextPoint ? (
                <div className="relative group cursor-pointer hover:scale-[1.01] transition-transform mx-2 md:mx-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-accent via-brand-accent/80 to-yellow-200 rounded-[2.5rem] blur-xl opacity-20" />
                    <div className="bg-white rounded-[2.5rem] p-5 md:p-8 shadow-xl border border-gray-100 relative overflow-hidden flex items-center justify-between">
                        <div>
                            <span className="text-[10px] font-black text-brand-accent uppercase tracking-[0.2em] mb-2 block flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-brand-accent animate-pulse" /> Nächster Programmpunkt</span>
                            <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-2 leading-tight">{nextPoint.title}</h3>
                            <div className="flex items-center gap-3 text-gray-500">
                                <div className="px-3 py-1 bg-gray-100 rounded-full flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-sm font-bold">{format(parseISO(nextPoint.datetime), 'HH:mm', { locale: de })} Uhr</span>
                                </div>
                            </div>
                        </div>
                        <div className="w-14 h-14 bg-yellow-50 rounded-[1.5rem] flex items-center justify-center text-brand-accent shadow-sm rotate-3">
                            <Calendar className="w-7 h-7" />
                        </div>
                    </div>
                </div>
            ) : !hasVisited ? (
                <div className="relative overflow-hidden rounded-[2rem] p-[1px] mx-2 md:mx-0 group mt-2">
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-accent via-purple-300 to-yellow-300 opacity-20 group-hover:opacity-40 blur-xl transition-all duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-accent/40 via-purple-300/40 to-yellow-300/40 rounded-[2rem]" />
                    <div className="relative bg-white/95 backdrop-blur-2xl rounded-[1.9rem] p-4 md:p-6 text-center border border-white/60 shadow-xl overflow-hidden">
                        <div className="absolute -top-16 -right-16 w-32 h-32 bg-brand-accent/10 rounded-full blur-2xl transition-colors duration-700" />

                        <div className="relative z-10 flex flex-col items-center">
                            <div className="relative">
                                <div className="absolute inset-0 bg-brand-accent blur-lg opacity-20 animate-pulse" />
                                <div className="w-14 h-14 relative bg-gradient-to-tr from-white to-gray-50 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mb-4 rotate-3 group-hover:rotate-12 transition-all duration-500">
                                    <Sparkles className="w-7 h-7 text-brand-accent drop-shadow-sm" />
                                </div>
                            </div>
                            <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-1 tracking-tight">Schön, dass du da bist!</h3>
                            <p className="text-gray-500 max-w-xs mx-auto leading-relaxed text-xs md:text-sm font-medium">
                                Teile besondere Momente, schreibe ins Gästebuch und erlebe das Event hautnah mit den anderen Gästen.
                            </p>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* 0. EVENT RECAP - CREATOR ONLY */}
            {isOwner && event?.date && (() => {
                try { return isPast(parseISO(event.date)); } catch (e) { return false; }
            })() && (
                    <div className="mx-2 md:mx-0 mb-6 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-[2.5rem] p-1 shadow-xl animate-in fade-in slide-in-from-top-4 duration-700">
                        <div className="bg-white rounded-[2.3rem] p-8 text-center relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
                            <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                                <Sparkles className="w-8 h-8 text-yellow-500" />
                            </div>
                            <h3 className="font-black text-2xl md:text-3xl text-gray-900 mb-2 uppercase tracking-tight">Dein Event-Recap</h3>
                            <p className="text-gray-500 max-w-sm mx-auto mb-6 text-sm font-medium leading-relaxed">
                                Das Event ist vorbei! Die KI hat die besten Momente gesammelt.
                            </p>
                            <button
                                onClick={() => summary ? onViewSummary() : onGenerateSummary()}
                                disabled={isGeneratingSummary}
                                className="w-full bg-gray-900 text-white font-black uppercase tracking-widest py-4 rounded-xl shadow-lg hover:scale-[1.02] transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isGeneratingSummary ? (
                                    <>Generiere... <span className="animate-pulse">✨</span></>
                                ) : (
                                    summary ? <>Highlight ansehen <Sparkles className="w-4 h-4 ml-1" /></> : <>Jetzt generieren <Sparkles className="w-4 h-4 ml-1" /></>
                                )}
                            </button>
                        </div>
                    </div>
                )}

            {/* 2.5 LATEST PHOTOS SLIDESHOW */}
            {mediaData.photos && mediaData.photos.length > 0 && (
                <div className="mx-2 md:mx-0 mb-6 relative group overflow-hidden">
                    <div className="flex items-center justify-between mb-3 px-1">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
                            <Camera className="w-3 h-3" /> Highlights
                        </h3>
                        <button
                            onClick={() => onOpenFeature('photo_upload')}
                            className="text-[10px] font-bold text-brand-accent uppercase tracking-widest flex items-center gap-1 hover:bg-brand-accent/10 transition-colors bg-brand-accent/5 px-3 py-1.5 rounded-full"
                        >
                            Zur Galerie <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>

                    <div className="relative w-full -mx-2 px-2 md:mx-0 md:px-0">
                        <div className="flex gap-4 overflow-x-auto pb-8 pt-6 px-4 md:px-2 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                            {mediaData.photos.slice(-20).reverse().map((photo, i) => (
                                <div key={i} className={`flex-none snap-center relative transition-all duration-500 hover:z-20 ${i % 2 === 0 ? '-translate-y-3 -rotate-2 hover:-translate-y-4' : 'translate-y-4 rotate-3 hover:translate-y-2'} hover:rotate-0`} style={{ width: 'clamp(140px, 40vw, 190px)', aspectRatio: '3/4' }}>
                                    <div
                                        className="w-full h-full rounded-[1.5rem] overflow-hidden shadow-md border-[2px] border-white relative group/img cursor-pointer transform-gpu transition-all duration-300 hover:shadow-2xl bg-gray-100"
                                        onClick={() => onOpenFeature('photo_upload')}
                                    >
                                        <img
                                            src={photo.url?.startsWith('http') ? photo.url : (photo.url?.startsWith('/') ? `${BACKEND_URL || ''}${photo.url}` : `${BACKEND_URL || ''}/api/media/${photo.url}`)}
                                            className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-700 pointer-events-none"
                                            alt=""
                                            loading="lazy"
                                        />
                                    </div>
                                </div>
                            ))}

                            {/* "More" Card at the end */}
                            <div className="flex-none snap-center relative" style={{ width: 'clamp(120px, 35vw, 160px)', aspectRatio: '3/4' }}>
                                <div
                                    onClick={() => onOpenFeature('photo_upload')}
                                    className="w-full h-full rounded-[1.5rem] bg-gray-50 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 cursor-pointer hover:bg-white hover:border-brand-accent transition-all duration-300 shadow-sm group/btn hover:shadow-md"
                                >
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm text-gray-400 group-hover/btn:text-brand-accent group-hover/btn:scale-110 transition-all border border-gray-100">
                                        <Images className="w-5 h-5" />
                                    </div>
                                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover/btn:text-brand-accent transition-colors text-center px-4 leading-relaxed">Alle ansehen</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 3. DYNAMIC FEED GRID */}
            {filteredFeatures.length > 0 && (
                <div className="space-y-4 px-2 md:px-0">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Feed & Funktionen</h3>
                        {!isUnlocked && <span className="text-[10px] font-bold text-red-500 bg-red-50 px-3 py-1 rounded-full flex items-center gap-1 border border-red-100"><Lock className="w-3 h-3" /> Locked</span>}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 auto-rows-auto">
                        {/* Existing feature widgets */}
                        {filteredFeatures.map((feature, i) => renderFeatureWidget(feature, i))}
                    </div>
                </div>
            )}

            {/* Floating Camera Button */}
            {isUnlocked && (
                <div className="fixed bottom-36 right-6 z-40 animate-in zoom-in duration-500 delay-500">
                    <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        ref={cameraInputRef}
                        className="hidden"
                        onChange={handleCameraUpload}
                    />
                    <button
                        onClick={() => !isUploading && cameraInputRef.current?.click()}
                        disabled={isUploading}
                        className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center shadow-2xl text-white hover:scale-110 transition-transform group"
                    >
                        {isUploading ? (
                            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <Camera className="w-6 h-6 group-hover:text-yellow-300 transition-colors" />
                        )}
                    </button>
                </div>
            )}

        </div>
    )
};

export default GuestFeed;
